import React, {FC, useEffect, useState} from "react"
import {withRouter, RouteComponentProps} from "react-router-dom"

import {Auth} from "@irati/auth/types"
import id from "@irati/utils/id"
import authentication from "@irati/auth"
import Icon from "@irati/ui/src/Icon"
import Flex from "@irati/ui/src/Flex"
import AppStoreBanner from "@irati/ui/src/AppContainer/StoreBanner"

import PropTypes from "prop-types"
import ProductTrackingNames from "@irati/products/enums/ProductTrackingNames"
import devToggles from "@irati/dev-toggles"
import HeaderLink from "../shared/HeaderLink"
import {HeaderContainer, HeaderWrapper, TenantLogo} from "../styled"
import MyProductAccount from "../shared/MyProductAccount"
import NavigationBackButton, {PRODUCT_LANDING_PAGES} from "../shared/NavigationBackButton"
import {SSOBusinessLogoWrapper, StyledLogo} from "../shared/styled"
import HeaderLoginMobile from "./HeaderLoginMobile"
import MobileMenuToggle from "./MobileMenuToggle"
import {SECONDARY_NAV_CONTEXT} from "./const"

import MobileWalletBalance from "./MobileWalletBalance"
import MobileNavigationMenu from "./MobileNavigationMenu"
import MobileMenuOptions from "./MobileMenuOptions"
import MobileNotificationMenu from "./MobileNotificationMenu"

interface Props extends RouteComponentProps {
  auth: Auth
  id?: string
  showAppBanner?: boolean
}

interface Context {
  apps: {[key: string]: string}
  appPath: string
}

const HeaderMobile: FC<Props> = (props, {appPath, apps: contextApps}: Context) => {
  const [showBackButton, setShowBackButton] = useState<boolean>(false)
  const [menuContext, setMenuContext] = useState<string>()
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false)

  const {auth, showAppBanner} = props
  const {user} = auth
  const {PERKS, HOME, RECOGNITION, MEDICAL, WELLNESS, WALLET, ACCOUNT, FLEXIREWARDS} = contextApps
  const isPerksSection = appPath === PERKS
  const isCelebrationSection = appPath === RECOGNITION
  const isHomepage = appPath === HOME
  const isLoggedIn = authentication.isLoggedIn()
  const isGlobalAdmin = authentication.isGlobalAdmin()
  const idNamespace = id("header", props, {force: true})
  const hasFlexiRewards = !!authentication.apps("wallet.flexiRewards")
  const showSecondaryNavIcon = (!hasFlexiRewards && isCelebrationSection) || (MEDICAL === appPath && isGlobalAdmin)

  useEffect(() => {
    if (isLoggedIn) {
      setShowBackButton(shouldShowBackButton(props.history.location.pathname))

      const removeHistoryListener = props.history.listen((location) => {
        setShowBackButton(shouldShowBackButton(location.pathname))
      })

      return () => removeHistoryListener()
    }
  }, [])

  if (!isLoggedIn) {
    return <HeaderLoginMobile businessLogoUrl={auth.branding.businessLogoUrl} />
  }

  const shouldShowBackButton = (currentPath: string) => {
    return !(props.history.location?.state?.prevUrl || PRODUCT_LANDING_PAGES.includes(currentPath.replace(/(\/)$/, "")))
  }

  const getProductName = () => {
    switch (appPath) {
      case PERKS:
        return ProductTrackingNames.perks
      case RECOGNITION:
        return ProductTrackingNames.recognition
      case MEDICAL:
        return ProductTrackingNames.medical
      case WELLNESS:
        return ProductTrackingNames.wellness
      case WALLET:
        return ProductTrackingNames.wallet
      case ACCOUNT:
        return ProductTrackingNames.account
      default:
        return ProductTrackingNames.home
    }
  }

  const togglePanel = (context?: string) => {
    const isContextChanged = context !== menuContext
    setMenuContext(context)
    setIsPanelOpen(isContextChanged || !isPanelOpen)
  }

  return (
    <HeaderWrapper showAppBanner={showAppBanner}>
      {isLoggedIn && (
        <Flex direction="column" width="100%">
          {showAppBanner && <AppStoreBanner />}
          <MobileMenuOptions
            isPanelOpen={isPanelOpen}
            menuContext={menuContext}
            showAppBanner={showAppBanner}
            togglePanel={togglePanel}
            id={id("mobileMenuOptions", idNamespace)}
          />

          <HeaderContainer>
            {devToggles.isSSOfromVivup() ? (
              <Flex alignItems="center" justifyContent="space-between" flex="1">
                <SSOBusinessLogoWrapper>
                  <StyledLogo src={auth.branding.businessLogoUrl} />
                </SSOBusinessLogoWrapper>
                {!showBackButton && <MyProductAccount isPerksSection={isPerksSection} isCelebrationSection={isCelebrationSection} />}
              </Flex>
            ) : (
              <>
                <Flex alignItems="center">
                  {showBackButton ? (
                    <NavigationBackButton productName={getProductName()} />
                  ) : (
                    <MobileNavigationMenu productName={getProductName()} />
                  )}
                  {isHomepage && <TenantLogo src={auth.branding.businessLogoUrl} />}
                </Flex>

                <Flex alignItems="center">
                  <Flex alignItems="center" justifyContent="space-between">
                    {auth.productPermissions?.platform?.contentCards && (
                      <MobileNotificationMenu id={idNamespace} user={user} togglePanel={togglePanel} />
                    )}
                    {!showBackButton && (
                      <>
                        <MyProductAccount isPerksSection={isPerksSection} isCelebrationSection={isCelebrationSection} />
                        <MobileWalletBalance togglePanel={togglePanel} />
                      </>
                    )}
                    <MobileMenuToggle id={idNamespace} user={user} togglePanel={togglePanel} />

                    {showSecondaryNavIcon && (
                      <HeaderLink onClick={() => togglePanel(SECONDARY_NAV_CONTEXT)}>
                        <Icon icon="mobileSecondaryNav" />
                      </HeaderLink>
                    )}
                  </Flex>
                </Flex>
              </>
            )}
          </HeaderContainer>
        </Flex>
      )}
    </HeaderWrapper>
  )
}

HeaderMobile.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string.isRequired
}

export default withRouter(HeaderMobile)
